import { motion } from 'framer-motion';
import { Radio, Users, Zap } from 'lucide-react';
import { WhatsAppIcon } from '@guridosdrones/ui';
import { FadeIn } from '@/components';

const GROUP_URL = 'https://chat.whatsapp.com/BU5TthNmrn3Amou0Pet65T';

const perks = [
  {
    icon: Radio,
    title: 'Dúvidas em tempo real',
    description: 'Tire dúvidas técnicas com quem já está operando no campo.',
  },
  {
    icon: Zap,
    title: 'Conteúdo em primeira mão',
    description: 'Atualizações, dicas e novidades antes de qualquer outro canal.',
  },
  {
    icon: Users,
    title: 'Rede em todo o Brasil',
    description: 'Conecte-se com pilotos da sua região e expanda sua atuação.',
  },
];

export function Community() {
  return (
    <section id="community" className="section-shell bg-background">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.07)_0%,transparent_60%)]" />

      <div className="section-container">
        <FadeIn direction="up" distance={24}>
          <div className="relative overflow-hidden border border-primary/20 bg-card shadow-[0_32px_100px_-40px_rgba(16,185,129,0.2)]">
            {/* Top accent */}
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-primary/60 to-transparent" />

            {/* Corner brackets */}
            {[
              'top-4 left-4',
              'top-4 right-4',
              'bottom-4 left-4',
              'bottom-4 right-4',
            ].map((pos, i) => (
              <svg
                key={pos}
                className={`absolute ${pos} h-4 w-4 text-primary/20`}
                viewBox="0 0 20 20"
                fill="none"
                role="img"
                aria-label="Decoração"
              >
                <path
                  d={
                    [
                      'M0 6V0h6',
                      'M20 6V0h-6',
                      'M0 14v6h6',
                      'M20 14v6h-6',
                    ][i]
                  }
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </svg>
            ))}

            <div className="flex flex-col gap-12 p-6 sm:p-10 lg:flex-row lg:items-center lg:gap-16 lg:p-14">

              {/* Copy side */}
              <div className="flex flex-col gap-7 lg:flex-1">
                <div>
                  <div className="mb-4 inline-flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                    </span>
                    <span className="font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
                      Comunidade
                    </span>
                  </div>

                  <h2 className="section-title leading-tight">
                    Você Não Precisa Evoluir{' '}
                    <span className="text-primary">Sozinho no Campo.</span>
                  </h2>
                </div>

                <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                  Centenas de pilotos trocam experiências, tiram dúvidas em tempo real e
                  constroem uma rede de trabalho no agro brasileiro — tudo dentro do grupo
                  oficial do Guri dos Drones.
                </p>

                <ul className="flex flex-col gap-5">
                  {perks.map((perk) => (
                    <li key={perk.title} className="flex items-start gap-4">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border border-primary/20 bg-primary/8 text-primary">
                        <perk.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{perk.title}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                          {perk.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="pt-2">
                  <a
                    href={GROUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex h-13 items-center gap-3 border border-primary/40 bg-primary/10 px-8 font-mono text-sm font-bold tracking-widest text-primary uppercase transition-all hover:border-primary/70 hover:bg-primary/20 sm:h-14"
                  >
                    <WhatsAppIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                    Entrar no Grupo Agora
                  </a>
                  <p className="mt-3 font-mono text-[10px] tracking-widest text-muted-foreground/40 uppercase">
                    Acesso gratuito · Grupo oficial
                  </p>
                </div>
              </div>

              {/* Visual side */}
              <div className="flex justify-center lg:flex-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="relative flex h-64 w-64 items-center justify-center sm:h-80 sm:w-80"
                >
                  {/* Outer rings */}
                  {[96, 72, 52].map((size, i) => (
                    <motion.div
                      key={size}
                      animate={{ scale: [1, 1.04, 1], opacity: [0.15, 0.3, 0.15] }}
                      transition={{
                        duration: 3 + i,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: i * 0.6,
                      }}
                      className="absolute rounded-full border border-primary/30"
                      style={{ width: `${size}%`, height: `${size}%` }}
                    />
                  ))}

                  {/* Center icon */}
                  <div className="relative z-10 flex h-24 w-24 items-center justify-center border-2 border-primary/40 bg-primary/10 shadow-[0_0_60px_rgba(16,185,129,0.25)]">
                    <WhatsAppIcon className="h-10 w-10 text-primary" />
                  </div>

                  {/* Floating node dots */}
                  {[
                    { id: 'n-top', top: '8%', left: '50%', delay: 0 },
                    { id: 'n-right', top: '50%', left: '92%', delay: 0.4 },
                    { id: 'n-br', top: '88%', left: '68%', delay: 0.8 },
                    { id: 'n-bl', top: '88%', left: '30%', delay: 1.2 },
                    { id: 'n-left', top: '50%', left: '8%', delay: 1.6 },
                    { id: 'n-tl', top: '18%', left: '18%', delay: 2.0 },
                    { id: 'n-tr', top: '18%', left: '80%', delay: 2.4 },
                  ].map((node) => (
                    <motion.div
                      key={node.id}
                      animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: node.delay,
                      }}
                      className="absolute h-2.5 w-2.5 rounded-full bg-primary/60"
                      style={{ top: node.top, left: node.left, transform: 'translate(-50%, -50%)' }}
                    />
                  ))}
                </motion.div>
              </div>

            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
